// Shared presentational primitives for the admin console pages.
//
// These were previously copy-pasted verbatim into Pipeline.tsx and
// SystemHealth.tsx (and the patterns recur across other pages). Centralizing
// them means a styling tweak happens once. Pure presentational — no data,
// no fetch (the only state is the ephemeral "copied ✓" flash in CopyId).
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

/** Titled section. Renders `err` as a Banner, otherwise wraps children in a bordered card. */
export function Section({
  title,
  err,
  children,
}: {
  title: string;
  err?: string;
  children?: ReactNode;
}) {
  return (
    <section className="mt-6">
      <h2 className="mb-2 text-sm font-semibold text-zinc-300">{title}</h2>
      {err ? (
        <Banner>{err}</Banner>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-zinc-800">{children}</div>
      )}
    </section>
  );
}

/** A single sortable-header descriptor for {@link Table}. */
export type TableColumn = { label: string; sortKey?: string };
/** Current sort state a page passes to {@link Table} to render the ▲/▼ affordance. */
export type SortState = { key: string; dir: "asc" | "desc" };

/**
 * Header row + tbody. `head` is the column labels; `children` are the <tr> rows.
 *
 * Backwards compatible: pass `string[]` for a plain header. Pass
 * `TableColumn[]` with `sortKey` + a `sort`/`onSort` pair to get clickable,
 * client-side-sortable headers (the page owns the sort of its own row array).
 */
export function Table({
  head,
  children,
  sort,
  onSort,
}: {
  head: (string | TableColumn)[];
  children: ReactNode;
  sort?: SortState;
  onSort?: (key: string) => void;
}) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-zinc-950 text-left text-xs uppercase tracking-wide text-zinc-500">
        <tr>
          {head.map((h, i) => {
            const label = typeof h === "string" ? h : h.label;
            const sortKey = typeof h === "string" ? undefined : h.sortKey;
            const sortable = !!sortKey && !!onSort;
            return (
              <th key={i} className="px-3 py-2 font-medium">
                {sortable ? (
                  <button
                    type="button"
                    onClick={() => onSort!(sortKey!)}
                    className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-zinc-300"
                  >
                    {label}
                    {sort?.key === sortKey && <span>{sort.dir === "asc" ? "▲" : "▼"}</span>}
                  </button>
                ) : (
                  label
                )}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}

/** Table cell. `mono` renders a smaller monospace value (ids, timestamps). */
export function Td({
  children,
  mono,
  className = "",
}: {
  children: ReactNode;
  mono?: boolean;
  className?: string;
}) {
  return (
    <td className={`px-3 py-2 ${mono ? "font-mono text-xs" : ""} ${className}`}>{children}</td>
  );
}

/** Green/red status pill. */
export function Badge({ ok, children }: { ok: boolean; children: ReactNode }) {
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-xs ${
        ok ? "bg-emerald-900 text-emerald-300" : "bg-red-900 text-red-300"
      }`}
    >
      {children}
    </span>
  );
}

/** Red error banner. Pass `className="mt-2"` for standalone (non-Section) placement. */
export function Banner({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`rounded border border-red-900 bg-red-950 px-3 py-2 text-sm text-red-300 ${className}`}
    >
      {children}
    </div>
  );
}

/** Stat tile with a status dot, big value, and optional sub-label. */
export function Tile({
  label,
  ok,
  value,
  sub,
}: {
  label: string;
  ok: boolean;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-3">
      <div className="flex items-center gap-2">
        <span
          className={`inline-block h-2 w-2 rounded-full ${ok ? "bg-emerald-400" : "bg-red-400"}`}
        />
        <span className="text-xs uppercase tracking-wide text-zinc-500">{label}</span>
      </div>
      <div className={`mt-1 text-lg ${ok ? "text-zinc-100" : "text-red-300"}`}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-zinc-500">{sub}</div>}
    </div>
  );
}

/**
 * Pill-styled link. External (http) links open in a new tab via a raw <a>;
 * internal hrefs route through react-router <Link> so they don't trigger a full
 * document reload (re-auth bootstrap + bundle re-download). (ADM-13)
 */
export function LinkPill({ href, children }: { href: string; children: ReactNode }) {
  const external = href.startsWith("http");
  const cls =
    "rounded border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-zinc-200 hover:bg-zinc-700";
  if (external) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={cls}>
        {children}
      </a>
    );
  }
  return (
    <Link to={href} className={cls}>
      {children}
    </Link>
  );
}

// ===========================================================================
// Promoted primitives (ADM-8, ADM-11, ADM-15) — previously re-implemented
// inline across many pages.
// ===========================================================================

/**
 * Underline-style tab button (Records / Logs). Kept distinct from {@link PillTabs}
 * because both idioms exist in the console; promoting them stops per-page drift.
 */
export function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
        active
          ? "border-zinc-100 text-zinc-100"
          : "border-transparent text-zinc-500 hover:text-zinc-300"
      }`}
    >
      {children}
    </button>
  );
}

/** Pill-style tab group (Moderation / Content / Claims / Promotions / Roles). */
export function PillTabs<T extends string>({
  tabs,
  active,
  onChange,
  className = "",
}: {
  tabs: { key: T; label: string }[];
  active: T;
  onChange: (key: T) => void;
  className?: string;
}) {
  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onChange(t.key)}
          className={`rounded px-3 py-1.5 text-sm ${
            active === t.key
              ? "bg-zinc-700 text-white"
              : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/** Search input + button (Records tabs / Users). Submits on Enter or click. */
export function SearchBar({
  value,
  onChange,
  onSearch,
  placeholder,
  loading,
  buttonLabel = "Search",
}: {
  value: string;
  onChange: (v: string) => void;
  onSearch: () => void;
  placeholder?: string;
  loading?: boolean;
  buttonLabel?: string;
}) {
  return (
    <div className="flex gap-2">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        onKeyDown={(e) => e.key === "Enter" && onSearch()}
        className="flex-1 rounded border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-500"
      />
      <button
        type="button"
        onClick={onSearch}
        disabled={loading}
        className="rounded bg-zinc-700 px-3 py-1.5 text-sm text-white hover:bg-zinc-600 disabled:opacity-50"
      >
        {loading ? "Loading…" : buttonLabel}
      </button>
    </div>
  );
}

/**
 * Truncated, monospace id with click-to-copy (ADM-8). The core operator loop is
 * "grab a UUID here, paste into SQL / another page" — this makes the full id
 * one click away instead of a pixel-precise selection of an 8-char stub.
 */
export function CopyId({
  id,
  chars = 8,
  className = "",
}: {
  id: string | null | undefined;
  chars?: number;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  if (!id) return <span className="font-mono text-xs text-zinc-600">—</span>;
  const short = id.length > chars ? `${id.slice(0, chars)}…` : id;

  async function copy() {
    try {
      await navigator.clipboard.writeText(id!);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // Clipboard unavailable (insecure context / older browser) — no-op; the
      // full id is still in the title tooltip for manual selection.
    }
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        void copy();
      }}
      title={`${id} — click to copy`}
      className={`group inline-flex max-w-full items-center gap-1 font-mono text-xs text-zinc-400 hover:text-zinc-200 ${className}`}
    >
      <span className="truncate">{short}</span>
      <span className={`shrink-0 ${copied ? "text-emerald-400" : "text-zinc-600 group-hover:text-zinc-400"}`}>
        {copied ? "✓" : "⧉"}
      </span>
    </button>
  );
}
